// V1 parity: applyPortalCOApprovals (public/app/index.html:20687–20720).
//
// THE FIX: the old usePortalCOApprovals hook correctly bumped local AppState
// (status, budget, activity log) but never wrote the approval back onto the
// portallink__ Firestore doc itself. That meant:
//   - portalChangeOrders[i].approvalStatus stayed 'pending'
//   - portalChangeOrders[i].appStatus stayed 'pending'
//   - portal.html kept showing the CO as awaiting GC approval
//   - syncPortalWithProject could overwrite the local approval with a stale
//     portallink snapshot on the next reload
//
// This hook adds the missing updateCOById call (step 1) that atomically flips
// approvalStatus + appStatus + portalApprovedAt on the shared/portallink__
// Firestore doc, THEN mirrors into local AppState (step 2), THEN re-syncs the
// portal snapshot (step 3). Steps 2–3 are identical to usePortalCOApprovals.
//
// Idempotency: skips any CO whose LOCAL AppState row is already
// status='approved' (source-of-truth guard). The Firestore appStatus field is
// intentionally NOT used as the primary guard: if updateCOById succeeds but
// saveAppState fails, the next snapshot sees Firestore-approved + local-pending
// and self-heals by re-attempting the AppState save. appliedRef prevents
// double-toast/double-bump within a session.
//
// Replaces usePortalCOApprovals — Shell.tsx mounts this and unmounts the old
// hook. The old file is preserved until Task 13 deletes it.

import { useEffect, useRef } from 'react'
import { onSnapshot, query, where } from 'firebase/firestore'
import { sharedCol, db } from '../lib/firebase/firestore'
import { useAuth } from './useAuth'
import { useQueryClient } from '@tanstack/react-query'
import { qk } from './queryKeys'
import { saveAppState } from '../lib/db'
import { syncPortalWithProject } from '../lib/portalSync'
import { updateCOById } from '../lib/portalLink'
import { toastOk } from '../lib/toastStore'
import { fmt } from '../lib/fmt'
import { makeActivityLogEntry } from '../lib/activityLog'
import type { AppState } from '../types/firestore'
import type { ChangeOrder, Project } from '../types'

// ── Types ─────────────────────────────────────────────────────────────────────

interface PortalCO {
  id: string
  coNum?: string
  name?: string
  description?: string
  amount?: number
  date?: string
  appStatus?: string
  approvalStatus?: string
  portalStatus?: string | null
  portalApprovedAt?: number
}

interface PortalDocLite {
  token: string
  ownerUid?: string
  clientName?: string
  projId?: number | null
  portalChangeOrders?: PortalCO[]
}

function coNumStr(id: number): string {
  return 'CO-' + String(id).padStart(3, '0')
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePortalLinkCOApproval(): void {
  const { effectiveUid: uid, isReady } = useAuth()
  const qc = useQueryClient()

  // Track (token:coId) pairs promoted this session to prevent double-toast/
  // double-bump across re-fires of the same snapshot.
  const appliedRef = useRef<Set<string>>(new Set())

  // Promise-chain serializer: queues snapshot callbacks so two approvals
  // arriving back-to-back don't race on saveAppState and drop each other.
  const queueRef = useRef<Promise<void>>(Promise.resolve())

  useEffect(() => {
    if (!uid || !isReady) return

    const q = query(sharedCol(), where('ownerUid', '==', uid))

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Collect portallink__ docs from this snapshot.
        const docs: PortalDocLite[] = []
        snap.forEach((docSnap) => {
          if (!docSnap.id.startsWith('portallink__')) return
          // Cast to the local PortalDocLite (not PortalLinkDoc) so we can
          // read clientName, which is written by portal.ts but not declared
          // in the canonical PortalLinkDoc type.
          const d = docSnap.data() as Partial<PortalDocLite>
          const entry: PortalDocLite = {
            token: docSnap.id.slice('portallink__'.length),
            portalChangeOrders: d.portalChangeOrders ?? [],
          }
          if (d.ownerUid)  entry.ownerUid  = d.ownerUid
          if (d.clientName) entry.clientName = d.clientName
          if (d.projId !== undefined) entry.projId = d.projId
          docs.push(entry)
        })

        queueRef.current = queueRef.current.then(async () => {
          // Read LIVE state at callback time — not at effect-mount time.
          // Closure-capturing state would miss COs added after mount.
          const s = qc.getQueryData<AppState>(qk.appstate(uid))
          if (!s) return

          // ── Collect pending approvals ─────────────────────────────────────
          interface Pending {
            token: string
            clientName: string
            co: PortalCO
            localCO: ChangeOrder
            project: Project
          }
          const pending: Pending[] = []
          for (const d of docs) {
            for (const pc of d.portalChangeOrders ?? []) {
              if (pc.portalStatus !== 'client-approved') continue

              const appKey = `${d.token}:${pc.id}`
              if (appliedRef.current.has(appKey)) continue

              const localCO = s.changeOrders.find((c) => String(c.id) === pc.id)
              if (!localCO) { appliedRef.current.add(appKey); continue }
              // Idempotency guard keyed on LOCAL AppState (source-of-truth).
              // This ensures that if Firestore was flipped but saveAppState
              // failed (partial failure), the next snapshot sees
              // Firestore-approved + local-pending and self-heals by
              // re-attempting the AppState save. The Firestore-side
              // appStatus guard is intentionally NOT used here — it would
              // permanently block recovery after a partial failure.
              if (localCO.status === 'approved') { appliedRef.current.add(appKey); continue }

              const project = s.projects.find((p) => p.id === localCO.projId)
              if (!project) continue

              pending.push({
                token: d.token,
                clientName: d.clientName || 'Client',
                co: pc,
                localCO,
                project,
              })
            }
          }

          if (pending.length === 0) {
            console.log('[portalLinkCOApproval] snapshot: no new approvals (applied:', appliedRef.current.size, ')')
            // Idempotent re-sync: portallink docs with client-approved COs
            // may have a stale coAdjustedTotal from a partial prior sync.
            const affected = new Set<number>()
            for (const d of docs) {
              const hasApproved = (d.portalChangeOrders ?? []).some((pc) => pc.portalStatus === 'client-approved')
              if (!hasApproved) continue
              for (const pc of d.portalChangeOrders ?? []) {
                if (pc.portalStatus !== 'client-approved') continue
                const localCO = s.changeOrders.find((c) => String(c.id) === pc.id)
                if (!localCO) continue
                affected.add(localCO.projId)
              }
            }
            for (const pid of affected) {
              try {
                await syncPortalWithProject(uid, pid, s)
                console.log('[portalLinkCOApproval] idempotent resync project', pid)
              } catch (err) {
                console.warn('[portalLinkCOApproval] idempotent resync failed', pid, err)
              }
            }
            return
          }

          console.log('[portalLinkCOApproval] promoting', pending.length, 'approvals:',
            pending.map((p) => `CO-${p.localCO.id}($${p.localCO.amount})`).join(', '))

          try {
            // ── Step 1: Atomically flip each CO on the portallink__ doc ──────
            // This is THE missing step — without it the portallink doc keeps
            // approvalStatus='pending' and appStatus='pending', which causes
            // the portal client page to show the CO as still awaiting GC
            // approval, and syncPortalWithProject to stomp the local flip on
            // the next reload.
            //
            // Do this BEFORE touching AppState. If the process dies between
            // steps the next snapshot sees Firestore-approved + local-pending
            // and self-heals: updateCOById no-ops (early return), then
            // saveAppState succeeds and the local guard fires on subsequent runs.
            for (const p of pending) {
              try {
                await updateCOById(db, p.token, String(p.localCO.id), (c) => {
                  // Idempotent: if the CO is already fully approved, return
                  // the same object reference so the portalLink transaction's
                  // equality check fires a no-op early return (no Firestore write).
                  if (c.appStatus === 'approved' && c.approvalStatus === 'approved') return c
                  return {
                    ...c,
                    approvalStatus: 'approved' as const,
                    appStatus: 'approved' as const,
                    // Preserve an existing portalApprovedAt so re-runs don't
                    // re-stamp a newer timestamp.
                    portalApprovedAt: c.portalApprovedAt ?? p.co.portalApprovedAt ?? Date.now(),
                  }
                })
                console.log('[portalLinkCOApproval] updateCOById OK', p.token, p.localCO.id)
              } catch (err) {
                // Non-fatal: the portallink doc may have been deleted or the
                // token may be stale. Log and continue — the AppState flip
                // below still has value (GC sees approval locally).
                console.warn('[portalLinkCOApproval] updateCOById failed', p.token, p.localCO.id, err)
              }
            }

            // ── Step 2: Mirror into local AppState ────────────────────────────
            // V1 L20705–20711: flip status, stamp approvedBy/portalStatus/
            // portalApprovedAt, bump project.budget, log activity.
            let next: AppState = s
            const affectedProjectIds = new Set<number>()

            for (const p of pending) {
              const amount = p.localCO.amount || 0
              affectedProjectIds.add(p.project.id)

              // Flip local CO — V1 L20705–20709.
              const newCOs = next.changeOrders.map((c): ChangeOrder => {
                if (c.id !== p.localCO.id) return c
                const u: ChangeOrder = {
                  ...c,
                  status: 'approved',
                  approvedBy: `${p.clientName} via portal`,
                }
                // V1 stamps portalStatus + portalApprovedAt for the portal
                // tab unread-badge and audit trail. These fields aren't in
                // the V2 ChangeOrder interface, so cast via unknown.
                ;(u as unknown as { portalStatus?: string; portalApprovedAt?: number })
                  .portalStatus = 'client-approved'
                ;(u as unknown as { portalStatus?: string; portalApprovedAt?: number })
                  .portalApprovedAt = p.co.portalApprovedAt ?? Date.now()
                return u
              })

              // Bump project.budget — V1 L20708.
              // Read from `next.projects` (not captured p.project) so
              // sequential approvals on the same project stack correctly.
              const newProjects = next.projects.map((pr): Project =>
                pr.id === p.project.id ? { ...pr, budget: (pr.budget || 0) + amount } : pr,
              )
              const updatedProject = newProjects.find((pr) => pr.id === p.project.id)
              console.log('[portalLinkCOApproval] CO-' + p.localCO.id, '+$' + amount,
                '→ project budget now $' + (updatedProject?.budget ?? '??'))

              // Activity log — V1 L20711.
              const entry = makeActivityLogEntry(
                uid,
                'project',
                p.project.id,
                `Client approved change order ${coNumStr(p.localCO.id)}`,
                {
                  amount,
                  description: p.localCO.description,
                  source: 'portal',
                  clientName: p.clientName,
                },
              )

              next = {
                ...next,
                changeOrders: newCOs,
                projects: newProjects,
                activityLog: [...next.activityLog, entry],
              }

              toastOk(
                `✅ Client approved ${coNumStr(p.localCO.id)}: ` +
                (amount >= 0 ? '+' : '') + fmt(amount) +
                (p.localCO.description ? ` — ${p.localCO.description}` : ''),
              )
            }

            // Mark applied BEFORE saveAppState so re-entrancy within this
            // session is blocked even if saveAppState is slow or retried.
            // The local-state guard (localCO.status === 'approved') is the
            // cross-session / cross-reload recovery path.
            for (const p of pending) {
              appliedRef.current.add(`${p.token}:${p.localCO.id}`)
            }

            qc.setQueryData(qk.appstate(uid), next)
            await saveAppState(uid, next)
            console.log('[portalLinkCOApproval] saveAppState complete')

            // ── Step 3: Rebuild portal snapshot ──────────────────────────────
            // Rewrites portalChangeOrders + coAdjustedTotal from the freshly-
            // approved local state so portal.html shows the correct Agreed
            // Total immediately (it prefers coAdjustedTotal over the raw sum).
            for (const projId of affectedProjectIds) {
              try {
                await syncPortalWithProject(uid, projId, next)
                console.log('[portalLinkCOApproval] portal re-synced project', projId)
              } catch (err) {
                console.warn('[portalLinkCOApproval] portal sync failed', projId, err)
              }
            }

          } catch (err) {
            console.warn('[portalLinkCOApproval] apply failed', err)
          }
        })
      },
      (err) => {
        console.warn('[portalLinkCOApproval] listener error', err)
      },
    )

    return () => {
      unsub()
      appliedRef.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isReady])
}
